import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { getRenderBufferHeight } from '../scene-manager.js';
import { laserClampChunk } from '../lighting/laser-array.js';

// BeamWall - 96 moving heads in a 12x8 wall, all of them dancing the SAME
// choreography at once. Techno, house, trance, festival.
//
// THE ONE STRUCTURAL CONSTRAINT, and everything here follows from it: 96
// additive full-frame volumetric cones that overlap are impossible on a tiled
// GPU. There is no volumetric cone anywhere in this file. A beam is ONE
// camera-facing quad with a pixel-floored screen width, exactly the primitive
// laser-cathedral.js invented and lighting/laser-array.js extracted, and all
// 96 of them live in a SINGLE InstancedMesh - one draw call.
//
// And no per-frame upload. The direction of head (col,row) is a pure function
// of the choreography parameters, and that function runs in the VERTEX shader
// (patDir below). What travels from JS to the GPU each frame is about a dozen
// scalars - the pattern id, the step counter, the ease, a colour - never 96
// directions and never 96 matrices. The housings are driven by the SAME
// function in their own vertex shader, which is also why a housing can never
// drift out of line with the beam leaving it: there is only one definition of
// where a head is pointing.
//
// MOVEMENT IS NOTCHED, NEVER SLID. A moving head does not glide; it snaps to
// its next position and holds. The choreography therefore advances on an
// INTEGER step counter that ticks once per beat, and the shader interpolates
// between step-1 and step over the first ~110ms only (uStepMix), then holds
// flat for the rest of the beat. A continuous time term would read as a slow
// pan and would lose the music entirely.

// Shared-rig contract (see the framing addendum). 'forbidden', the same call
// laser-cathedral.js and laser-grid.js make and for the same reason: a wash
// on the deck would destroy the contrast the beams need to read as light.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'beamwall',
  family: 'stage',
  energyLevel: 0.92,
  bpmMin: 118,
  bpmMax: 185,
  movement: 0.90,
  // The brief's own figure. Worth knowing before it is read as a bug: the
  // danceability pool picks the profiles NEAREST the track's index, and real
  // tracks essentially never index above the low 0.8s, so at 0.94 this entry
  // sits past every track in the same way lasergrid's original 0.88 did (see
  // the comment on its own PROFILE). Lowering it to ~0.80 is a one-line
  // change if it turns out never to be selected.
  danceability: 0.94,
  tonality: 0.55,
  prefers: { gridStrength: [0.65, 1.0], lowEnd: [0.45, 1.0] },
  enabled: true
};

// --- the wall --------------------------------------------------------------
// 12 x 8 at 1.0 spacing is the brief's plot. The lower tiers keep the wall's
// FOOTPRINT and thin the grid instead of shrinking it: a 8x4 wall at spacing
// 1.0 would be a small square of lights in the middle of the frame, which is
// a different image, not a cheaper one. Spacing is therefore derived from the
// span, and only the count changes.
const TIER_LAYOUT = {
  2: { cols: 12, rows: 8 },   // 96
  1: { cols: 10, rows: 6 },   // 60
  0: { cols: 8, rows: 4 }     // 32
};
const DEFAULT_TIER = 2;
const SPAN_X = 11.0;          // 12 columns at spacing 1.0
const SPAN_Y = 7.0;           // 8 rows at spacing 1.0
const WALL_Z = -14.0;
const ROW_CENTER_Y = 5.0;
const LENS_OFFSET = 0.22;     // where the beam leaves the housing
// CONVERGE meets over the crowd. The point is chosen in SCREEN space and
// unprojected (see _pickConverge): the two things that matter about it are
// both facts about the frame - it must be VISIBLE, and it must not be on the
// cover - and neither can be stated in world coordinates without solving for
// the camera. The first version picked world points on a circle and rejected
// the ones that landed on the cover; every survivor turned out to be 45
// degrees off-axis, i.e. off-screen entirely. Measured, not noticed.
const CONVERGE_DIST = 12.0;    // from the camera, so it sits in the room
const CONVERGE_MIN_Y = 2.0;    // never below head height
const CONVERGE_R_MIN = 0.50;   // height units from the cover's own centre
const CONVERGE_R_MAX = 0.80;
const CONVERGE_EDGE = 0.90;    // and never closer than this to the frame edge

// Where the audience stands: in front of the wall, below it, looking up at
// it. A VERTICAL wall at the back of the stage firing HORIZONTALLY at the
// room - the beams come at the viewer, and that is the whole effect.
//
// An earlier plot tilted every beam up by 20 degrees to keep them clear of
// the lens. It read as a floor of fixtures firing at the sky, seen from
// above, which is the opposite image. The base direction is now +z and
// nothing else: any pitch a beam has is its pattern's own, symmetric about
// the wall's centre line.
//
// THE CAMERA STANDS BEYOND THE BEAMS' REACH, and that is a constraint, not a
// taste: a beam dies at BEAM_REACH (25 units), so with the audience 21 units
// out the beams were still at a quarter of their brightness as they swept
// past the lens and they filled the frame edge to edge. At 27 units they die
// about six units short of the viewer - in the haze, in front of us, which
// is what the brief asks for. The lens is lengthened to match so that moving
// back does not shrink the wall.
const CAMERA_FOV = 34;
// y is a compromise: lower is a better contre-plongee, higher opens the
// floor so that RAIN's 96 impacts read as a grid instead of as a band.
const CAMERA_POS = [0.0, 2.5, 13.0];
const CAMERA_LOOK = [0, 5.0, -14.0];
const FOG_DENSITY = 0.05;     // the brief's own figure
const FLOOR_COLOR = 0x090b0f;
const FLOOR_SIZE = 90;
const BACKDROP_COLOR = 0x05070a;
// EVERY SURFACE COLOUR HERE IS PITCHED AGAINST THE FOG, and the numbers look
// wrong until you do that arithmetic. FogExp2 at the brief's mandatory 0.05
// leaves exp(-(0.05*27)^2) = 16% of a surface at the wall's distance, so a
// truss painted the dark grey it actually is arrives at 2% and disappears.
// These are the colours that LAND right, not the colours the objects are.
const TRUSS_COLOR = 0x6b7686;
const CABLE_COLOR = 0x424a58;
// Same arithmetic for the lens: it has to clear the bloom threshold (0.78)
// AFTER the 16%, which is what makes 96 of them read as lamps rather than as
// coloured dots.
const LENS_BOOST = 9.0;
const LENS_WHITE = 0.35;

// --- the beam --------------------------------------------------------------
// A MOVING HEAD IS NOT A LASER, and none of what follows is laser-grid's
// geometry. laser-array.js's billboard holds a MINIMUM screen width and lets
// a world-sized beam stay a hairline at distance, because a laser IS a
// hairline. A lyre throws a CONE: it leaves the lens about 18px across, opens
// to about 60px by the time it dies, and it dies - it never crosses the whole
// frame. All four of those are the brief's own figures, implemented literally.
//
// The width law is in PIXELS rather than world units on purpose. A world cone
// read as a hairline at the back of the room and as a wall of light at the
// front; in pixels the cone keeps its silhouette wherever the fixture is,
// which is what lets 96 of them read as one gesture.
const BEAM_NEAR_PX = 18.0;
const BEAM_FAR_PX = 60.0;
// Where the beam dies, in world units, and how fast. The extinction is
// EXPONENTIAL - exp(-d * BEAM_EXT) - because that is what a beam in haze
// does, and the last stretch is faded hard to zero so nothing ever reaches
// the edge of the frame. exp(-25 * 0.13) = 0.039, so the smoothstep only has
// to clear the last 4%.
// The rate is what makes the CONE read, and the first pass got it wrong in a
// way only measurement showed: at exp(-0.13d) the halo dropped under the
// visible floor long before the quad had opened, so the beam's measured width
// went 7.6px -> 3px - it got THINNER as it travelled, the opposite of a cone.
// The extinction is gentler now and the close is done by the hard ramp, so
// the beam is still carrying light while it is wide.
const BEAM_REACH = 25.0;
const BEAM_EXT = 0.075;
const BEAM_FADE_FROM = 14.0;
// The cross-section: core out to 40% of the half-width, coloured halo beyond,
// which is the brief's profile. LINEAR values - the renderer is ACES + sRGB,
// so what lands on screen is roughly value^(1/2.2).
// Tuned against the measured frame, not by eye: at 0.72/0.42 a full wall
// lit 65% of the pixels at mean luminance 0.22, which is a white-out, not a
// light show. These land it near 40% and 0.13 with the cone profile intact.
const BEAM_CORE_W = 0.44;
const BEAM_HALO_W = 0.28;
const BEAM_HOT_MIX = 0.70;   // a lyre beam is COLOURED; only a laser is white
const PEAK_LUMINANCE = 0.92;
// Sized against the CONE, not against a laser dot: a 60px beam arriving on
// the floor cannot leave a 0.4-unit spot without reading as a beam that was
// cut off rather than one that landed.
const IMPACT_SIZE = 0.85;
const IMPACT_HOT_MIX = 0.85;

// The shared safe-zone ramp bottoms out at 0.32 (SAFE_MIN_ATTENUATION in
// shaders/safeZone.glsl.js). The brief asks for 0.45 here, because a wall of
// beams crossing the cover at 0.32 reads as a smear rather than as light.
// Remapped, NOT edited at the source: every other visualizer depends on that
// constant. If the shared floor ever moves, this number moves with it.
const SHARED_SAFE_FLOOR = 0.32;
const SAFE_FLOOR = 0.45;

// --- choreography ----------------------------------------------------------
const PAT_FAN = 0, PAT_CROSS = 1, PAT_WAVE = 2, PAT_RIPPLE = 3;
const PAT_CONVERGE = 4, PAT_RAIN = 5, PAT_CHASE = 6, PAT_STORM = 7;
const PATTERN_COUNT = 8;
const PATTERN_NAMES = {
  fan: PAT_FAN, cross: PAT_CROSS, wave: PAT_WAVE, ripple: PAT_RIPPLE,
  converge: PAT_CONVERGE, rain: PAT_RAIN, chase: PAT_CHASE, storm: PAT_STORM
};

// How long a head takes to reach its next position. Real moving heads are
// fast but not instant; 110ms is inside a 16th at 180bpm, so the arrival is
// always well clear of the next notch and the hold is always the majority of
// the beat.
const MOVE_MS = 110;
// A pattern change is the one thing that is NOT notched: it crossfades over
// two beats so the wall reads as reconfiguring rather than as cutting.
const PATTERN_FADE_BEATS = 2;
// The bar sweep: a gain bump that crosses the wall left to right over the
// first beat of every bar.
const SWEEP_BEATS = 1.0;
const SWEEP_BOOST = 1.5;
const SWEEP_SHARP = 60.0;
// The half-beat of black that sets up a phrase change. A phrase is 16 beats,
// so half a beat is 1/32 of it.
const BLACKOUT_PHRASE_FRAC = 1 / 32;

// The drop, in beats from its own start: converge, then explosion, then
// white. See _stepDrop.
const DROP_CONVERGE_BEATS = 2.0;
const DROP_BURST_BEATS = 0.4;
const DROP_WHITE_BEATS = 1.2;
const DROP_TOTAL_BEATS = DROP_CONVERGE_BEATS + DROP_BURST_BEATS + DROP_WHITE_BEATS;

const BEAT_PULSE_AMOUNT = 0.55;
const BEAT_PULSE_DECAY = 13;

const LASER_SATURATION = 0.95;
const LASER_LIGHTNESS = 0.55;
const FALLBACK_COLORS = [0x3060ff, 0xff2060];

// Synthetic band envelopes for the case the brief's material most often hits:
// no radiopi timeline, so director.bands is all zeros. Lifted from
// gallery.js's SYNTH_BASE block - step-shaped, triggered by phase wraps.
const SYNTH_BASE = 0.10;
const SYNTH_DECAY = 1.9;
const SYNTH_PERIOD_LOW = 4.7;
const SYNTH_PERIOD_MID = 3.1;
const SYNTH_PERIOD_HIGH = 6.3;
// When trackFeatures carries no tempo either - cascade.js's own figure.
const SYNTH_BPM = 124;

// Radians of fan per world unit from the wall's centre. The furthest fixture
// is 6.52 units out, so the widest the fan opens is about 24 degrees - inside
// the frame, which is what stops a fan reading as a starburst leaving on all
// four sides.
const FAN_RATE = 0.064;

const DEG = Math.PI / 180;

// ---------------------------------------------------------------------------
// The choreography, in GLSL. This is the whole of BeamWall's motion and it is
// shared verbatim by the beams, the floor impacts and the housings.
// ---------------------------------------------------------------------------
const CHOREO_CHUNK = `
  uniform float uCols;
  uniform float uRows;
  uniform float uSpacingX;
  uniform float uSpacingY;
  uniform float uPat;          // pattern being danced now
  uniform float uPatPrev;      // the one it is crossfading out of
  uniform float uPatMix;       // 0 = prev, 1 = current
  uniform float uStep;         // integer notch counter, one per beat
  uniform float uStepMix;      // 0 the instant the notch fires, 1 once arrived
  uniform vec3  uConverge;     // CONVERGE target for this step
  uniform vec3  uConvergePrev; // and for the one before it
  uniform float uBurst;        // the drop's explosion, 0..1
  uniform float uSpread;       // bands.low -> how wide the wall opens

  // Every hash input is bounded before it reaches sin(): mediump is real fp16
  // on the TV and an unbounded argument overflows to NaN there and only there.
  float bwHash(float n) { return fract(sin(mod(n, 6283.1853)) * 43758.5453); }

  vec3 bwHeadPos(float col, float row) {
    return vec3((col - (uCols - 1.0) * 0.5) * uSpacingX,
                ${ROW_CENTER_Y.toFixed(2)} + (row - (uRows - 1.0) * 0.5) * uSpacingY,
                ${WALL_Z.toFixed(2)});
  }

  // Base direction is +z: straight out of the wall, at the audience.
  vec3 bwAngles(float yaw, float pitch) {
    float cp = cos(pitch);
    return normalize(vec3(sin(yaw) * cp, sin(pitch), cos(yaw) * cp));
  }

  // The direction head (col,row) points at choreography step s.
  // gain: CHASE is the only pattern that lights part of the wall at a time.
  vec3 bwPatDir(float pat, float col, float row, float id, float s, out float gain) {
    float cx = uCols > 1.0 ? (col / (uCols - 1.0)) * 2.0 - 1.0 : 0.0;
    float cy = uRows > 1.0 ? (row / (uRows - 1.0)) * 2.0 - 1.0 : 0.0;
    vec3 o = bwHeadPos(col, row);
    float grp = mod(col + row, 2.0);
    gain = 1.0;
    float yaw = 0.0;
    float pitch = 0.0;
    // The global opening: bands.low, and the drop's explosion. GLOBAL, so it
    // scales every fixture by the same number and cannot disperse them.
    float bm = uSpread * (1.0 + uBurst * 1.6);

    if (pat < 0.5) {
      // FAN - the wall opens as one shutter. The angle off the wall's normal
      // grows LINEARLY with the fixture's distance from the wall's centre and
      // the beam leans exactly along its own radius. Written as an axis and
      // an angle rather than as a yaw and a pitch on purpose: yaw/pitch are
      // two independent scales, and a fan built from them is an ellipse with
      // a seam down the middle, not a fan. One function, 96 fixtures, no
      // per-head term of any kind.
      vec2 off = vec2(o.x, o.y - ${ROW_CENTER_Y.toFixed(2)});
      float d = length(off);
      float amt = 0.35 + 0.65 * abs(sin(s * 1.0471975512));
      float a = d * ${FAN_RATE.toFixed(5)} * amt * bm;
      vec2 nrm = d > 0.0001 ? off / d : vec2(0.0, 1.0);
      return normalize(vec3(nrm * sin(a), cos(a)));
    } else if (pat < 1.5) {
      // CROSS - the only pattern that splits the wall. Even and odd heads
      // aim at opposite points and their beams cross in the middle of the
      // room; the two targets swap every step.
      float sw = mod(s, 2.0) < 1.0 ? 1.0 : -1.0;
      float sgn = (grp < 0.5 ? 1.0 : -1.0) * sw;
      vec3 t = vec3(sgn * 7.0, 8.6 + 2.0 * sin(s * 0.7853981634), 1.5);
      vec3 cd = normalize(t - o);
      vec3 od = bwAngles(cx * 0.9, cy * 0.7);
      return normalize(mix(cd, od, uBurst));
    } else if (pat < 2.5) {
      // WAVE - ONE sine travelling across the columns, and the spatial
      // frequency is the whole point: at cx*2.4 the wall carried three
      // quarters of a period, neighbouring columns differed by 25 degrees and
      // their beams crossed into a thicket. Under one period across the wall,
      // and neighbours stay within about 9 degrees of each other, which is
      // what lets the eye follow the wave instead of the individual beams.
      yaw = 0.40 * sin(cx * 1.1 + cy * 0.35 + s * 0.7853981634);
      pitch = cy * 0.26 - 0.04;
    } else if (pat < 3.5) {
      // RIPPLE - the same wave, but radial from the wall's centre.
      float d = length(vec2(cx, cy));
      float a = sin(d * 3.4 - s * 0.7853981634);
      yaw = cx * (0.20 + 0.42 * a);
      pitch = cy * (0.20 + 0.42 * a);
    } else if (pat < 4.5) {
      // CONVERGE - every beam on one point. uBurst blows it open again.
      vec3 tgt = (s < uStep - 0.5) ? uConvergePrev : uConverge;
      vec3 cd = normalize(tgt - o);
      vec3 od = bwAngles(cx * 0.9, cy * 0.7);
      return normalize(mix(cd, od, uBurst));
    } else if (pat < 5.5) {
      // RAIN - every fixture on the SAME vector, raking down and forward, so
      // the 96 impacts land as a grid: the wall's own lattice, sheared across
      // the floor. It used to give each column a random phase, which is
      // exactly the kind of per-head term that makes a wall look like noise.
      // The angle is now the only thing that steps.
      float ph = 0.5 + 0.5 * sin(s * 0.7853981634);
      pitch = -(0.55 + 0.45 * ph) * bm;
      yaw = 0.0;
    } else if (pat < 6.5) {
      // CHASE - one column at a time, its neighbours trailing.
      float head = mod(s, uCols);
      float d = abs(col - head);
      d = min(d, uCols - d);
      gain = d < 0.5 ? 1.0 : (d < 1.5 ? 0.35 : 0.06);
      yaw = cx * 0.10;
      pitch = cy * 0.14 - 0.04;
    } else {
      // STORM - every head on its own, re-rolled on every notch.
      float n = mod(id * 7.13 + s * 31.7, 4096.0);
      yaw = (bwHash(n) * 2.0 - 1.0) * 0.72;
      pitch = (bwHash(n + 11.3) * 2.0 - 1.0) * 0.46 - 0.06;
    }

    // RAIN already applied bm to its own pitch (an absolute angle, not an
    // offset), so it opts out here.
    float k = (pat > 4.5 && pat < 5.5) ? 1.0 : bm;
    return bwAngles(yaw * k, pitch * k);
  }

  // One pattern, notched: where it was last step, where it is this step, and
  // the short ease between them. uStepMix holds at 1 for most of the beat, so
  // what this returns is a HOLD punctuated by a snap, not a slide.
  vec3 bwStepDir(float pat, float col, float row, float id, out float gain) {
    float gA;
    float gB;
    vec3 dA = bwPatDir(pat, col, row, id, uStep - 1.0, gA);
    vec3 dB = bwPatDir(pat, col, row, id, uStep, gB);
    gain = mix(gA, gB, uStepMix);
    vec3 m = mix(dA, dB, uStepMix);
    float ml = length(m);
    return ml > 0.0001 ? m / ml : dB;
  }

  // Both patterns, crossfaded. The branch is on a uniform, so it is coherent
  // across the whole draw and the second evaluation costs nothing at all
  // outside the two beats of a phrase change.
  vec3 bwHeadDir(float col, float row, float id, out float gain) {
    float gB;
    vec3 dB = bwStepDir(uPat, col, row, id, gB);
    if (uPatMix > 0.999) { gain = gB; return dB; }
    float gA;
    vec3 dA = bwStepDir(uPatPrev, col, row, id, gA);
    gain = mix(gA, gB, uPatMix);
    vec3 m = mix(dA, dB, uPatMix);
    float ml = length(m);
    return ml > 0.0001 ? m / ml : dB;
  }
`;

// The wall is ONE colour. CROSS is the exception the brief allows, and it is
// an exception because the whole point of CROSS is that you can see which
// half of the wall a given beam came from.
const COLOR_CHUNK = `
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform float uWhite;

  vec3 bwPatColor(float pat, float grp) {
    return (pat > 0.5 && pat < 1.5 && grp > 0.5) ? uColorB : uColorA;
  }

  vec3 bwColor(float grp) {
    vec3 c = mix(bwPatColor(uPatPrev, grp), bwPatColor(uPat, grp), uPatMix);
    return mix(c, vec3(1.0), uWhite);
  }
`;

// The bar sweep, and the safe zone on its own floor.
const GAIN_CHUNK = `
  uniform float uMasterGain;
  uniform float uSweep;   // 0..1 across the columns, or -1 when idle
  uniform float uSolo;    // ?beamsolo=<id>: one fixture only, or -1

  float bwSoloGate(float id) {
    return uSolo < -0.5 ? 1.0 : step(abs(id - uSolo), 0.5);
  }

  float bwSweepGain(float col) {
    if (uSweep < -0.5) return 1.0;
    float ct = uCols > 1.0 ? col / (uCols - 1.0) : 0.0;
    float d = ct - uSweep;
    return 1.0 + ${SWEEP_BOOST.toFixed(2)} * exp(-d * d * ${SWEEP_SHARP.toFixed(1)});
  }

  float bwSafeAtt(vec4 clipPos) {
    float a = safeZoneAttenuation(clipPos);
    return ${SAFE_FLOOR.toFixed(2)}
         + (a - ${SHARED_SAFE_FLOOR.toFixed(2)})
         * ${((1 - SAFE_FLOOR) / (1 - SHARED_SAFE_FLOOR)).toFixed(6)};
  }
`;

// The cone's billboard. Same camera-facing construction and the same
// degenerate-axis guard as laser-array.js's (that part is hard-won and
// unchanged), but the WIDTH LAW is the opposite one: a screen width
// interpolated from BEAM_NEAR_PX at the lens to BEAM_FAR_PX at the end,
// instead of a world width with a pixel floor.
const BILLBOARD_CHUNK = `
  uniform float uPixelScale;   // pixels per world unit at unit view depth
  uniform float uNearPx;
  uniform float uFarPx;

  vec3 bwPerp(vec3 n) {
    vec3 a = abs(n.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    return normalize(cross(n, a));
  }

  vec3 bwBillboard(vec3 along, vec3 dir, float t, float sideSign, out float guard) {
    vec3 toCam = cameraPosition - along;
    float camDist = length(toCam);
    toCam = camDist > 0.0001 ? toCam / camDist : vec3(0.0, 0.0, 1.0);

    // Two separate concerns, never folded into one factor that could reach 0:
    // the SIDE vector genuinely degenerates looking down the axis (bwPerp
    // handles that); the opacity should merely dip.
    float align = abs(dot(dir, toCam));
    guard = mix(0.30, 1.0, 1.0 - smoothstep(0.90, 0.999, align));

    vec3 side = cross(dir, toCam);
    float sideLen = length(side);
    side = sideLen > 0.0001 ? side / sideLen : bwPerp(dir);

    vec4 mv = modelViewMatrix * vec4(along, 1.0);
    float viewDepth = max(-mv.z, 0.001);
    float worldPerPx = viewDepth / uPixelScale;
    float halfWidth = mix(uNearPx, uFarPx, t) * 0.5 * worldPerPx;
    return along + side * (halfWidth * sideSign);
  }
`;

const beamVertexShader = `
  precision highp float;

  attribute float iCol;
  attribute float iRow;

  uniform float uBeamLen;

  varying float vSide;
  varying float vDist;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafeAtt;

  ${CHOREO_CHUNK}
  ${COLOR_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${GAIN_CHUNK}
  ${BILLBOARD_CHUNK}

  void main() {
    float id = iRow * uCols + iCol;
    float gain;
    vec3 dir = bwHeadDir(iCol, iRow, id, gain);
    vec3 origin = bwHeadPos(iCol, iRow) + dir * ${LENS_OFFSET.toFixed(3)};

    float t = position.y;                 // 0 at the lens, 1 at the far end
    vec3 along = origin + dir * (t * uBeamLen);

    float guard;
    vec3 worldPos = bwBillboard(along, dir, t, position.x, guard);

    vSide = position.x;
    vDist = t * uBeamLen;          // world units travelled, for the extinction
    vColor = bwColor(mod(iCol + iRow, 2.0));
    vGain = uMasterGain * gain * guard * bwSweepGain(iCol) * bwSoloGate(id);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vSafeAtt = bwSafeAtt(gl_Position);
  }
`;

// The cone's cross-section and its extinction. Nothing here is shared with
// laser-array.js's beam fragment, which draws a two-pixel white core inside a
// thin halo - that is a laser. This is a lyre: a wide soft core, a coloured
// halo out to the edge of the cone, and an exponential death in the haze.
const beamFragmentShader = `
  precision mediump float;

  uniform float uPulse;

  varying float vSide;    // -1..1 across the cone
  varying float vDist;    // world units from the lens
  varying vec3 vColor;
  varying float vGain;
  varying float vSafeAtt;

  ${laserClampChunk(PEAK_LUMINANCE)}

  void main() {
    float u = abs(vSide);

    // The brief's profile: core out to 40% of the half-width, halo beyond.
    float core = 1.0 - smoothstep(0.0, 0.40, u);
    // Gentler than a square: the halo IS the cone's silhouette, and squaring
    // it pulled the visible edge back in towards the core.
    float e = max(1.0 - u, 0.0);
    float halo = e * sqrt(e);

    // Exponential extinction, then a hard close so the beam cannot reach the
    // edge of the frame however loud the track gets.
    float atten = exp(-vDist * ${BEAM_EXT.toFixed(3)})
                * (1.0 - smoothstep(${BEAM_FADE_FROM.toFixed(1)}, ${BEAM_REACH.toFixed(1)}, vDist));

    vec3 hot = mix(vColor, vec3(1.0), ${BEAM_HOT_MIX.toFixed(2)});
    vec3 col = vColor * halo * ${BEAM_HALO_W.toFixed(3)}
             + hot * core * ${BEAM_CORE_W.toFixed(3)};
    col *= atten * uPulse * vGain * vSafeAtt;

    // AdditiveBlending is (SRC_ALPHA, ONE): all of the intensity lives in rgb
    // and alpha stays 1, or it would be squared.
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// Where a beam meets the floor. Same instance layout as the beams, same
// direction function; the quad simply collapses (within = 0) for any head
// whose beam is not pointing down, or whose floor crossing is past the end of
// the beam.
const impactVertexShader = `
  precision highp float;

  attribute float iCol;
  attribute float iRow;

  uniform float uBeamLen;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  ${CHOREO_CHUNK}
  ${COLOR_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${GAIN_CHUNK}

  void main() {
    float id = iRow * uCols + iCol;
    float gain;
    vec3 dir = bwHeadDir(iCol, iRow, id, gain);
    vec3 origin = bwHeadPos(iCol, iRow) + dir * ${LENS_OFFSET.toFixed(3)};

    float down = max(-dir.y, 0.0001);
    float td = origin.y / down;
    float within = step(0.02, -dir.y) * step(td, uBeamLen);

    vec3 hit = origin + dir * td;
    hit.y = 0.02;                         // clear of the floor plane

    // A grazing beam smears its dot along the direction it is travelling.
    vec3 fwd = normalize(vec3(dir.x, 0.0, dir.z) + vec3(0.0, 0.0, 0.0001));
    vec3 side = vec3(-fwd.z, 0.0, fwd.x);
    float stretch = 1.0 / max(abs(dir.y), 0.18);

    vec3 worldPos = hit
                  + fwd * (position.x * ${IMPACT_SIZE.toFixed(2)} * stretch)
                  + side * (position.z * ${IMPACT_SIZE.toFixed(2)});

    vUv = position.xz;
    vColor = bwColor(mod(iCol + iRow, 2.0));
    // A long throw lands faint - the same falloff the beam itself carries.
    float reach = 1.0 - smoothstep(0.55, 1.0, td / uBeamLen);
    vGain = uMasterGain * gain * within * reach * bwSweepGain(iCol) * bwSoloGate(id);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vGain *= bwSafeAtt(gl_Position);
  }
`;

// What the cone leaves where it lands. laser-array.js's impact is a 16%-wide
// hard dot inside a cubic halo, which is right for a laser and wrong for a
// 60px beam: it reads as a beam that was cut off rather than one that
// arrived. Same profile as the cone itself - core out to 40%, halo beyond.
const impactFragmentShader = `
  precision mediump float;

  uniform float uPulse;

  varying vec3 vColor;
  varying float vGain;
  varying vec2 vUv;

  ${laserClampChunk(PEAK_LUMINANCE)}

  void main() {
    // vUv is the quad's own frame, so the ellipse is already stretched with
    // it and the distance here is uniform whatever the incidence.
    float r = length(vUv);
    float core = 1.0 - smoothstep(0.0, 0.40, r);
    float e = max(1.0 - r, 0.0);
    float halo = e * sqrt(e);

    vec3 hot = mix(vColor, vec3(1.0), ${IMPACT_HOT_MIX.toFixed(2)});
    vec3 col = vColor * halo * 0.34 + hot * core * 0.55;
    col *= uPulse * vGain;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// A LYRE, not a can. Three parts, and the split is what makes the pivot read:
// the CLAMP does not move, the FORK pans about the vertical, and the HEAD
// pans AND tilts inside the fork. A single object rotated onto the beam
// direction has no fork and therefore no visible axis of rotation - it just
// looks like a dot that changes shade.
//
// Both moving parts derive their orientation from bwHeadDir, the same
// function the beam uses, so a fixture cannot point anywhere other than where
// its own beam goes.
const FIXTURE_CHUNK = `
  // Right-handed frame whose +z is f. Degenerate only when f is vertical,
  // which the guard handles by rolling the reference axis.
  vec3 bwRight(vec3 f) {
    vec3 up = abs(f.y) > 0.985 ? vec3(0.0, 0.0, 1.0) : vec3(0.0, 1.0, 0.0);
    return normalize(cross(up, f));
  }
`;

// panOnly: the fork, which turns about the vertical and never tilts. Without
// it, false: the head, which does both.
function makeFixtureVertexShader(panOnly) {
  return `
  precision highp float;

  attribute float iCol;
  attribute float iRow;
  attribute float aLens;

  uniform float uFogDensity;
  uniform float uPulse;

  varying vec3 vNormalW;
  varying vec3 vColor;
  varying float vLens;
  varying float vFog;

  ${CHOREO_CHUNK}
  ${COLOR_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${GAIN_CHUNK}
  ${FIXTURE_CHUNK}

  void main() {
    float id = iRow * uCols + iCol;
    float gain;
    vec3 dir = bwHeadDir(iCol, iRow, id, gain);
    ${panOnly ? `
    // The fork keeps its feet on the floor: only the horizontal part of the
    // beam direction turns it.
    vec3 flat3 = vec3(dir.x, 0.0, dir.z);
    float fl = length(flat3);
    vec3 f = fl > 0.01 ? flat3 / fl : vec3(0.0, 0.0, 1.0);
    vec3 r = bwRight(f);
    vec3 u = vec3(0.0, 1.0, 0.0);` : `
    vec3 f = dir;
    vec3 r = bwRight(f);
    vec3 u = cross(f, r);`}

    vec3 pivot = bwHeadPos(iCol, iRow);
    vec3 world = pivot + r * position.x + u * position.y + f * position.z;
    vNormalW = r * normal.x + u * normal.y + f * normal.z;

    vLens = aLens;
    // The lens follows its own beam: the pattern's gain (CHASE lights part of
    // the wall at a time), the blackout, and the beat.
    vColor = bwColor(mod(iCol + iRow, 2.0)) * gain * uMasterGain * uPulse
           * bwSweepGain(iCol) * bwSoloGate(id);

    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    // FogExp2, by hand: the fog colour is black, so fogging is a multiply.
    float fd = uFogDensity * length(mv.xyz);
    gl_Position = projectionMatrix * mv;
    // One varying carries both multiplies: the haze, and the cover's own
    // attenuation - the wall sits directly behind the artwork.
    vFog = exp(-fd * fd) * bwSafeAtt(gl_Position);
  }
`;
}

// The scene carries no lights at all (RIG 'forbidden' - a wash would kill the
// beams), so the fixtures carry their own two-term shading. LINEAR values:
// the renderer is ACES + sRGB, so what lands on screen is about v^(1/2.2).
// They are pitched to survive the fog, which at the wall's distance is a
// multiply of about 0.16.
const fixtureFragmentShader = `
  precision mediump float;

  uniform float uLensGain;

  varying vec3 vNormalW;
  varying vec3 vColor;
  varying float vLens;
  varying float vFog;

  void main() {
    vec3 n = normalize(vNormalW);
    float lam = max(dot(n, normalize(vec3(-0.35, 0.85, 0.40))), 0.0);
    vec3 body = vec3(0.10) + vec3(0.34) * lam;
    vec3 lens = mix(vColor, vec3(1.0), ${LENS_WHITE.toFixed(2)})
              * uLensGain * ${LENS_BOOST.toFixed(2)};
    vec3 col = mix(body, lens, vLens) * vFog;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- fixture geometry -------------------------------------------------------
// Built by hand rather than merged from three.js primitives, because every
// vertex needs the aLens attribute and BufferGeometryUtils is not in this
// bundle. Boxes carry real per-face normals so the two-term shading has
// something to work with.

function pushBox(P, N, L, I, cx, cy, cz, sx, sy, sz, lens) {
  const hx = sx / 2, hy = sy / 2, hz = sz / 2;
  const faces = [
    [[0, 0, 1], [[-hx, -hy, hz], [hx, -hy, hz], [hx, hy, hz], [-hx, hy, hz]]],
    [[0, 0, -1], [[hx, -hy, -hz], [-hx, -hy, -hz], [-hx, hy, -hz], [hx, hy, -hz]]],
    [[1, 0, 0], [[hx, -hy, hz], [hx, -hy, -hz], [hx, hy, -hz], [hx, hy, hz]]],
    [[-1, 0, 0], [[-hx, -hy, -hz], [-hx, -hy, hz], [-hx, hy, hz], [-hx, hy, -hz]]],
    [[0, 1, 0], [[-hx, hy, hz], [hx, hy, hz], [hx, hy, -hz], [-hx, hy, -hz]]],
    [[0, -1, 0], [[-hx, -hy, -hz], [hx, -hy, -hz], [hx, -hy, hz], [-hx, -hy, hz]]]
  ];
  for (let f = 0; f < faces.length; f++) {
    const n = faces[f][0], c = faces[f][1];
    const base = P.length / 3;
    for (let i = 0; i < 4; i++) {
      P.push(cx + c[i][0], cy + c[i][1], cz + c[i][2]);
      N.push(n[0], n[1], n[2]);
      L.push(lens);
    }
    I.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
}

function pushDisc(P, N, L, I, cx, cy, cz, r, seg, lens) {
  const base = P.length / 3;
  P.push(cx, cy, cz); N.push(0, 0, 1); L.push(lens);
  for (let i = 0; i <= seg; i++) {
    const a = (i / seg) * Math.PI * 2;
    P.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r, cz);
    N.push(0, 0, 1); L.push(lens);
  }
  for (let i = 0; i < seg; i++) I.push(base, base + 1 + i, base + 2 + i);
}

function finishGeometry(P, N, L, I, instanced) {
  const g = instanced ? new THREE.InstancedBufferGeometry() : new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(P), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(N), 3));
  g.setAttribute('aLens', new THREE.BufferAttribute(new Float32Array(L), 1));
  g.setIndex(I);
  return g;
}

// The head: body, snout, and the lens disc that is the only lit face.
function buildHeadGeometry() {
  const P = [], N = [], L = [], I = [];
  pushBox(P, N, L, I, 0, 0, -0.03, 0.40, 0.40, 0.46, 0);
  pushBox(P, N, L, I, 0, 0, 0.235, 0.31, 0.31, 0.10, 0);
  pushDisc(P, N, L, I, 0, 0, 0.288, 0.137, 14, 1);
  return finishGeometry(P, N, L, I, true);
}

// The fork: two arms the head swings between, and the crossbar under it.
function buildYokeGeometry() {
  const P = [], N = [], L = [], I = [];
  pushBox(P, N, L, I, -0.255, 0, -0.03, 0.075, 0.54, 0.13, 0);
  pushBox(P, N, L, I, 0.255, 0, -0.03, 0.075, 0.54, 0.13, 0);
  pushBox(P, N, L, I, 0, -0.295, -0.03, 0.59, 0.095, 0.17, 0);
  pushBox(P, N, L, I, 0, -0.375, -0.03, 0.14, 0.09, 0.14, 0);
  return finishGeometry(P, N, L, I, true);
}

// The truss the wall is bolted to: one horizontal bar per row, diagonal
// bracing, two uprights, and a clamp under every fixture. Entirely static, so
// it is baked into ONE geometry in world coordinates and drawn once. The
// clamp matters more than it looks: it is the part that does NOT move, and
// a fork only reads as turning against something that does not.
function buildTrussGeometry(cols, rows, sx, sy) {
  const P = [], N = [], L = [], I = [];
  const halfW = ((cols - 1) * sx) / 2 + 0.75;
  const z = WALL_Z - 0.40;
  for (let r = 0; r < rows; r++) {
    const y = ROW_CENTER_Y + (r - (rows - 1) / 2) * sy - 0.40;
    pushBox(P, N, L, I, 0, y, z, halfW * 2, 0.11, 0.11, 0);
    for (let c = 0; c < cols - 1; c++) {
      const x = (c - (cols - 1) / 2 + 0.5) * sx;
      pushBox(P, N, L, I, x, y + sy * 0.5, z - 0.07, sx * 0.85, 0.05, 0.05, 0);
    }
  }
  const topY = ROW_CENTER_Y + ((rows - 1) / 2) * sy + 0.55;
  const botY = ROW_CENTER_Y - ((rows - 1) / 2) * sy - 0.60;
  const posts = [-halfW, halfW];
  for (let i = 0; i < posts.length; i++) {
    pushBox(P, N, L, I, posts[i], (topY + botY) / 2, z, 0.14, topY - botY, 0.14, 0);
  }
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = (c - (cols - 1) / 2) * sx;
      const y = ROW_CENTER_Y + (r - (rows - 1) / 2) * sy;
      pushBox(P, N, L, I, x, y - 0.40, z + 0.11, 0.13, 0.13, 0.32, 0);
      pushBox(P, N, L, I, x, y - 0.355, WALL_Z - 0.03, 0.16, 0.10, 0.16, 0);
    }
  }
  return finishGeometry(P, N, L, I, false);
}

// Power and data, looping from each fixture back to its bar. Two segments
// each: they are a couple of dark pixels and their whole job is to stop the
// fixtures reading as floating.
function buildCableGeometry(cols, rows, sx, sy) {
  const pts = [];
  const z = WALL_Z - 0.40;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = (c - (cols - 1) / 2) * sx;
      const y = ROW_CENTER_Y + (r - (rows - 1) / 2) * sy - 0.40;
      const sag = Math.min(0.24, sy * 0.26);
      const mx = x + sx * 0.32;
      pts.push(x, y, z + 0.07, mx, y - sag, z - 0.03);
      pts.push(mx, y - sag, z - 0.03, x + sx * 0.62, y, z);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts), 3));
  return g;
}

// Compiled once per tier and NEVER released. three.js frees a program the
// moment its last material is disposed, so a visualizer that comes round
// again in the rotation recompiles everything it owns - MEASURED here on the
// Pi at 4.8 to 5.2 seconds for this file's three programs, on every single
// appearance (building the meshes themselves is 10-210ms; it is all compile).
// Same fix as nightdrive.js's MATERIALS and nebula.js's VOLUME_MATERIALS.
//
// THE UNIFORM OBJECTS ARE CACHED WITH THEM, and that is the part that is easy
// to get wrong: three.js resolves a material's uniform list once and keeps
// the objects it resolved against, so handing the material a NEW uniforms
// object afterwards changes nothing it reads. The visualizer therefore ADOPTS
// the cached objects and resets, in _resetUniforms, the handful of values
// update() does not write every frame.
const MATERIALS = new Map();

function tierMaterials(tier) {
  const cached = MATERIALS.get(tier);
  if (cached) return cached;

  const layout = TIER_LAYOUT[tier];
  const uniforms = Object.assign({}, safeZoneUniforms, {
    uTime: { value: 0 },
    uPulse: { value: 1 },
    uMasterGain: { value: 1 },
    uNearPx: { value: BEAM_NEAR_PX },
    uFarPx: { value: BEAM_FAR_PX },
    uPixelScale: { value: 1 },
    uBeamLen: { value: BEAM_REACH },
    uCols: { value: layout.cols },
    uRows: { value: layout.rows },
    uSpacingX: { value: layout.cols > 1 ? SPAN_X / (layout.cols - 1) : 1 },
    uSpacingY: { value: layout.rows > 1 ? SPAN_Y / (layout.rows - 1) : 1 },
    uPat: { value: PAT_FAN },
    uPatPrev: { value: PAT_FAN },
    uPatMix: { value: 1 },
    uStep: { value: 0 },
    uStepMix: { value: 1 },
    uConverge: { value: new THREE.Vector3(0, 6.0, -4.0) },
    uConvergePrev: { value: new THREE.Vector3(0, 6.0, -4.0) },
    uBurst: { value: 0 },
    uSpread: { value: 1 },
    uSweep: { value: -1 },
    uSolo: { value: -1 },
    uFogDensity: { value: FOG_DENSITY },
    uWhite: { value: 0 },
    uLensGain: { value: 1 },
    uColorA: { value: new THREE.Color(FALLBACK_COLORS[0]) },
    uColorB: { value: new THREE.Color(FALLBACK_COLORS[1]) }
  });

  const entry = {
    uniforms: uniforms,
    beam: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: beamVertexShader,
      fragmentShader: beamFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true
    }),
    impact: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: impactVertexShader,
      fragmentShader: impactFragmentShader,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true,
      // DoubleSide, and it is not optional. The base quad is authored in the
      // xz plane with a winding whose normal is -Y, and the vertex shader
      // then re-bases it onto (fwd, side), which flips handedness again. Seen
      // from a camera ABOVE the floor every one of the 96 is a back face:
      // they drew 192 triangles a frame and lit exactly zero pixels, which is
      // what a flat-colour fragment test finally showed. Same trap as
      // ravesign.js's tubes.
      side: THREE.DoubleSide
    }),
    head: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: makeFixtureVertexShader(false),
      fragmentShader: fixtureFragmentShader,
      transparent: false,
      depthWrite: true,
      depthTest: true
    }),
    yoke: new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: makeFixtureVertexShader(true),
      fragmentShader: fixtureFragmentShader,
      transparent: false,
      depthWrite: true,
      depthTest: true
    }),
    truss: new THREE.MeshBasicMaterial({ color: TRUSS_COLOR, fog: true }),
    cable: new THREE.LineBasicMaterial({ color: CABLE_COLOR, fog: true })
  };
  MATERIALS.set(tier, entry);
  return entry;
}

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}

function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }

function hueDistance(a, b) {
  const d = Math.abs(a - b) % 1;
  return d > 0.5 ? 1 - d : d;
}

export class BeamWallVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    // ?beammode=fan|cross|wave|ripple|converge|rain|chase|storm pins one
    // pattern, which is how a single motif gets looked at on its own.
    const forced = urlParam('beammode');
    this._forcedPattern = forced != null && PATTERN_NAMES[forced] !== undefined
      ? PATTERN_NAMES[forced] : null;
    // ?beamstep=1 publishes the live choreography state on window.__beamwall.
    // Criterion 2 is "notched, not sliding", and that is a claim about a
    // TIME SERIES - it cannot be judged from a screenshot, so it gets
    // measured from this instead of eyeballed.
    this._probe = urlParam('beamstep') === '1';
    // ?beamlens=1 hides the beams and leaves the housings, so the pivot can
    // be watched on its own.
    this._lensOnly = urlParam('beamlens') === '1';
    // ?beamsolo=<id> lights ONE fixture (id = row * cols + col) and gates
    // the other 95. The cone's width law is specified in pixels, and a pixel
    // claim has to be measured off a rendered frame rather than asserted;
    // with 96 overlapping cones there is nothing to measure.
    const solo = parseFloat(urlParam('beamsolo'));
    this._solo = Number.isFinite(solo) ? solo : -1;
    // ?beamlen=<float> - see the note on ?beamcam.
    const bl = parseFloat(urlParam('beamlen'));
    this._beamLen = Number.isFinite(bl) && bl > 0 ? bl : BEAM_REACH;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    // Written once, never per frame: scene-manager's drift re-bases position
    // and quaternion together, and a visualizer that writes only one of them
    // each frame makes the drift compound. Writing neither is safe.
    // ?beamcam=x,y,z,lx,ly,lz overrides the plot - the framing of a wall of
    // beams is not a thing that can be reasoned out on paper, and this is how
    // CAMERA_POS/CAMERA_LOOK above were arrived at.
    // A LONG LENS, and it is not a detail. At the shared 68 degrees an
    // 11-unit wall 20 units away subtends a fifth of the frame and reads as a
    // point source with a starburst around it - measured, not guessed, over
    // six framings. A festival beam shot is taken on a long lens for exactly
    // this reason: it is what compresses a wall into a wall. scene-manager
    // restores BASE_FOV before every init(), so this cannot leak (see its own
    // note on ocean.js).
    camera.fov = CAMERA_FOV;
    camera.updateProjectionMatrix();

    const cam = (urlParam('beamcam') || '').split(',').map(parseFloat);
    const P = cam.length === 6 && cam.every(Number.isFinite) ? cam.slice(0, 3) : CAMERA_POS;
    const L = cam.length === 6 && cam.every(Number.isFinite) ? cam.slice(3, 6) : CAMERA_LOOK;
    camera.position.set(P[0], P[1], P[2]);
    camera.lookAt(L[0], L[1], L[2]);

    scene.fog = new THREE.FogExp2(0x000000, FOG_DENSITY);

    const layout = TIER_LAYOUT[this._tier];
    this._cols = layout.cols;
    this._rows = layout.rows;
    this._headCount = this._cols * this._rows;

    const mats = tierMaterials(this._tier);
    this._uniforms = mats.uniforms;
    this._beamMaterial = mats.beam;
    this._impactMaterial = mats.impact;
    this._headMaterial = mats.head;
    this._yokeMaterial = mats.yoke;
    this._trussMaterial = mats.truss;
    this._cableMaterial = mats.cable;
    this._resetUniforms();

    this._buildFloor();
    this._buildBackdrop();
    this._buildBeams();
    this._buildImpacts();
    this._buildFixtures();
    this._buildTruss();

    // --- choreography state ---
    this._time = 0;
    this._step = 0;
    this._sinceStep = 0;
    this._beatPeriod = 0.5;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._lastMode = null;
    this._pat = this._forcedPattern != null ? this._forcedPattern : PAT_FAN;
    this._patPrev = this._pat;
    this._patFade = 1;
    this._stormDue = false;
    this._patOrder = this._shuffledPatterns();
    this._patCursor = 0;
    this._sweepT = -1;
    this._dropT = -1;
    this._spread = 1;
    this._converge = new THREE.Vector3(0, 6.0, -4.0);
    this._convergePrev = this._converge.clone();
    this._probeVec = new THREE.Vector3();
    this._synth = { low: SYNTH_BASE, mid: SYNTH_BASE, high: SYNTH_BASE };
    this._synthPhase = { low: null, mid: null, high: null };
    this._lastPaletteRef = undefined;
    this._scratch = new THREE.Color();
    this._hsl = { h: 0, s: 0, l: 0 };
    this._applyPalette(null);
    this._pickConverge();
  }

  // --- geometry --------------------------------------------------------------

  _buildFloor() {
    this._floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    this._floorMaterial = new THREE.MeshBasicMaterial({ color: FLOOR_COLOR });
    this._floor = new THREE.Mesh(this._floorGeometry, this._floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    this._scene.add(this._floor);
  }

  _buildBackdrop() {
    // A plane just behind the wall, so the housings read as mounted on
    // something rather than floating in the fog.
    this._backGeometry = new THREE.PlaneGeometry(46, 26);
    this._backMaterial = new THREE.MeshBasicMaterial({ color: BACKDROP_COLOR });
    this._backdrop = new THREE.Mesh(this._backGeometry, this._backMaterial);
    this._backdrop.position.set(0, 8, WALL_Z - 1.2);
    this._scene.add(this._backdrop);
  }

  _instanceAttributes(geometry) {
    const n = this._headCount;
    const iCol = new Float32Array(n);
    const iRow = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      iCol[i] = i % this._cols;
      iRow[i] = Math.floor(i / this._cols);
    }
    geometry.setAttribute('iCol', new THREE.InstancedBufferAttribute(iCol, 1));
    geometry.setAttribute('iRow', new THREE.InstancedBufferAttribute(iRow, 1));
    geometry.instanceCount = n;
  }

  _buildBeams() {
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0
    ]), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    this._instanceAttributes(geometry);

    this._beamGeometry = geometry;
    this._beams = new THREE.Mesh(geometry, this._beamMaterial);
    this._beams.frustumCulled = false;
    this._beams.visible = !this._lensOnly;
    this._scene.add(this._beams);
  }

  _buildImpacts() {
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1
    ]), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    this._instanceAttributes(geometry);

    this._impactGeometry = geometry;
    this._impacts = new THREE.Mesh(geometry, this._impactMaterial);
    this._impacts.frustumCulled = false;
    this._impacts.visible = !this._lensOnly;
    this._scene.add(this._impacts);
  }

  // The fixtures. TWO instanced draws, one for the forks and one for the
  // heads, because they turn about different axes - and it is exactly that
  // difference that makes 96 of them visibly swing together. Both read
  // bwHeadDir, so neither can disagree with its own beam, and neither uploads
  // anything per frame.
  _buildFixtures() {
    this._yokeGeometry = buildYokeGeometry();
    this._instanceAttributes(this._yokeGeometry);
    this._yokes = new THREE.Mesh(this._yokeGeometry, this._yokeMaterial);
    this._yokes.frustumCulled = false;
    this._scene.add(this._yokes);

    this._headGeometry = buildHeadGeometry();
    this._instanceAttributes(this._headGeometry);
    this._heads = new THREE.Mesh(this._headGeometry, this._headMaterial);
    this._heads.frustumCulled = false;
    this._scene.add(this._heads);
  }

  _buildTruss() {
    const sx = this._uniforms.uSpacingX.value;
    const sy = this._uniforms.uSpacingY.value;
    this._trussGeometry = buildTrussGeometry(this._cols, this._rows, sx, sy);
    this._truss = new THREE.Mesh(this._trussGeometry, this._trussMaterial);
    this._truss.frustumCulled = false;
    this._scene.add(this._truss);

    this._cableGeometry = buildCableGeometry(this._cols, this._rows, sx, sy);
    this._cables = new THREE.LineSegments(this._cableGeometry, this._cableMaterial);
    this._cables.frustumCulled = false;
    this._scene.add(this._cables);
  }

  // The values update() does not write on every frame, brought back to their
  // start state - the uniform objects outlive the visualizer (see MATERIALS),
  // so without this a second BeamWall would open mid-drop, or white, or with
  // the previous track's colours.
  _resetUniforms() {
    const u = this._uniforms;
    const pat = this._forcedPattern != null ? this._forcedPattern : PAT_FAN;
    u.uBeamLen.value = this._beamLen;
    u.uPat.value = pat;
    u.uPatPrev.value = pat;
    u.uPatMix.value = 1;
    u.uStep.value = 0;
    u.uStepMix.value = 1;
    u.uSweep.value = -1;
    u.uSolo.value = this._solo;
    u.uBurst.value = 0;
    u.uWhite.value = 0;
    u.uSpread.value = 1;
    u.uLensGain.value = 1;
    u.uMasterGain.value = 1;
    u.uPulse.value = 1;
    u.uTime.value = 0;
    u.uConverge.value.set(0, 6.0, -4.0);
    u.uConvergePrev.value.set(0, 6.0, -4.0);
  }

  // --- palette ---------------------------------------------------------------

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : FALLBACK_COLORS;
    const hues = [];
    for (let i = 0; i < vivid.length; i++) {
      this._scratch.set(vivid[i]).getHSL(this._hsl);
      hues.push(this._hsl.h);
    }
    let a = hues.length ? hues[0] : 0.62;
    let b = (a + 0.5) % 1;
    if (hues.length >= 2) {
      // CROSS needs its two halves to be TELLABLE APART, so the second hue is
      // the one furthest from the first rather than simply the next one.
      let best = -1;
      for (let i = 1; i < hues.length; i++) {
        const d = hueDistance(a, hues[i]);
        if (d > best) { best = d; b = hues[i]; }
      }
      if (best < 0.12) b = (a + 0.5) % 1;
    }
    this._uniforms.uColorA.value.setHSL(a, LASER_SATURATION, LASER_LIGHTNESS);
    this._uniforms.uColorB.value.setHSL(b, LASER_SATURATION, LASER_LIGHTNESS);
  }

  // --- choreography ----------------------------------------------------------

  _shuffledPatterns() {
    // STORM is the ONLY pattern with a per-fixture term, and it is the
    // exception that proves the other seven are synchronous - so it joins
    // every other cycle, which brings it round about once in fourteen
    // phrases instead of once in eight.
    const order = [];
    for (let i = 0; i < PATTERN_COUNT; i++) {
      if (i === PAT_STORM && !this._stormDue) continue;
      order.push(i);
    }
    this._stormDue = !this._stormDue;
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const t = order[i]; order[i] = order[j]; order[j] = t;
    }
    return order;
  }

  // The convergence point. The brief forbids it landing inside the safe zone,
  // and "inside" is a SCREEN fact, not a world one - so the candidates are
  // projected and the first one that clears the cover wins. Projection, not
  // an inverse: there is nothing to solve, only to test.
  _pickConverge() {
    this._convergePrev.copy(this._converge);
    // R19: the zone to clear is the centre of composition's, not the cover's.
    const cx = safeZoneUniforms.uStageCenter.value.x;
    const cy = safeZoneUniforms.uStageCenter.value.y;
    const hx = safeZoneUniforms.uStageHalfSize.value.x + safeZoneUniforms.uSafeFeather.value;
    const hy = safeZoneUniforms.uStageHalfSize.value.y + safeZoneUniforms.uSafeFeather.value;
    const aspect = safeZoneUniforms.uAspect.value || 1;
    if (!this._camera) return;

    const start = Math.floor(Math.random() * 12);
    for (let k = 0; k < 12; k++) {
      const ang = ((start + k) % 12) * 30 * DEG;
      const r = CONVERGE_R_MIN + Math.random() * (CONVERGE_R_MAX - CONVERGE_R_MIN);
      // Wider than tall, because the frame is and the room is.
      const sx = cx + Math.cos(ang) * r * 1.35;
      const sy = cy + Math.sin(ang) * r;
      if (Math.abs(sx - cx) <= hx && Math.abs(sy - cy) <= hy) continue; // on the cover
      const ndcX = sx / aspect;
      if (Math.abs(ndcX) > CONVERGE_EDGE || Math.abs(sy) > CONVERGE_EDGE) continue;
      if (this._unprojectTo(ndcX, sy, this._converge)) return;
    }
    // Nothing cleared - park it just left of the cover rather than on it.
    const sx = cx - hx - 0.14;
    this._unprojectTo(Math.max(-CONVERGE_EDGE, sx / aspect), cy, this._converge);
  }

  // ndc (x, y) -> the world point CONVERGE_DIST in front of the camera along
  // that ray. Returns false if it would land below head height.
  _unprojectTo(ndcX, ndcY, out) {
    this._probeVec.set(ndcX, ndcY, 0.5).unproject(this._camera);
    this._probeVec.sub(this._camera.position).normalize()
      .multiplyScalar(CONVERGE_DIST).add(this._camera.position);
    if (this._probeVec.y < CONVERGE_MIN_Y) return false;
    out.copy(this._probeVec);
    return true;
  }

  _advanceStep() {
    this._step++;
    this._sinceStep = 0;
    if (this._pat === PAT_CONVERGE || this._dropT >= 0) this._pickConverge();
  }

  _changePattern() {
    if (this._forcedPattern != null) return;
    this._patPrev = this._pat;
    this._patCursor++;
    if (this._patCursor >= this._patOrder.length) {
      this._patOrder = this._shuffledPatterns();
      this._patCursor = 0;
      // Never open a fresh cycle on the pattern that just finished.
      if (this._patOrder[0] === this._patPrev) {
        const t = this._patOrder[0];
        this._patOrder[0] = this._patOrder[1];
        this._patOrder[1] = t;
      }
    }
    this._pat = this._patOrder[this._patCursor];
    this._patFade = 0;
  }

  _stepSynthBands(deltaTime, profile) {
    const k = Math.exp(-SYNTH_DECAY * deltaTime);
    const sy = this._synth;
    sy.low = SYNTH_BASE + (sy.low - SYNTH_BASE) * k;
    sy.mid = SYNTH_BASE + (sy.mid - SYNTH_BASE) * k;
    sy.high = SYNTH_BASE + (sy.high - SYNTH_BASE) * k;

    const fired = (key, phase) => {
      const prev = this._synthPhase[key];
      this._synthPhase[key] = phase;
      return prev != null && phase < prev;
    };

    const bar = profile.barPhase;
    const beat = profile.beatPhase;
    if (bar != null && beat != null) {
      if (fired('low', bar)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }
    const t = this._time;
    if (fired('low', (t / SYNTH_PERIOD_LOW) % 1)) sy.low = 1.0;
    if (fired('mid', (t / SYNTH_PERIOD_MID) % 1)) sy.mid = 0.85;
    if (fired('high', (t / SYNTH_PERIOD_HIGH) % 1)) sy.high = 0.75;
  }

  // director.hasReliableGrid is exactly "a radiopi timeline exists", which is
  // exactly when director.bands carries real values rather than zeros.
  _effectiveBands(director) {
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  // The drop: converge, explode, white. Driven in beats so it keeps its shape
  // at any tempo.
  _stepDrop(deltaTime) {
    if (this._dropT < 0) return;
    this._dropT += deltaTime / this._beatPeriod;
    const u = this._uniforms;
    if (this._dropT < DROP_CONVERGE_BEATS) {
      u.uBurst.value = 0;
      u.uWhite.value = 0;
    } else if (this._dropT < DROP_CONVERGE_BEATS + DROP_BURST_BEATS) {
      const t = (this._dropT - DROP_CONVERGE_BEATS) / DROP_BURST_BEATS;
      u.uBurst.value = t * t;
      u.uWhite.value = t;
    } else if (this._dropT < DROP_TOTAL_BEATS) {
      const t = (this._dropT - DROP_CONVERGE_BEATS - DROP_BURST_BEATS) / DROP_WHITE_BEATS;
      u.uBurst.value = 1;
      u.uWhite.value = 1 - t;
    } else {
      this._dropT = -1;
      u.uBurst.value = 0;
      u.uWhite.value = 0;
    }
  }

  update(deltaTime, profile) {
    if (profile.playbackState !== 'PLAYING') return; // gel

    this._time += deltaTime;
    this._sinceStep += deltaTime;
    const director = profile.director;
    const u = this._uniforms;

    // The tempo lives on trackFeatures, not on the profile root - and a
    // track with no known tempo still has to be danced to, so the notch
    // falls back to SYNTH_BPM rather than to a frozen wall.
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;

    this._stepSynthBands(deltaTime, profile);
    const bands = this._effectiveBands(director);

    // --- the notch ---
    // A beat is one notch. Where there is no grid at all, the notch falls
    // back to the tempo clock rather than to nothing: a wall of moving heads
    // that stops moving is not a degraded version of this image, it is a
    // different one.
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) this._advanceStep();
      this._lastBeatPhase = beatPhase;
    } else if (this._sinceStep >= this._beatPeriod) {
      this._advanceStep();
    }
    u.uStep.value = this._step;
    // The ease, and only the ease: past MOVE_MS this holds flat at 1 for the
    // rest of the beat. That hold is what makes the motion read as a moving
    // head rather than as a pan.
    const e = clamp01(this._sinceStep * 1000 / MOVE_MS);
    u.uStepMix.value = e * e * (3 - 2 * e);

    // --- the bar sweep ---
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) {
      if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._sweepT = 0;
      this._lastBarPhase = barPhase;
    }
    if (this._sweepT >= 0) {
      this._sweepT += deltaTime / (this._beatPeriod * SWEEP_BEATS);
      u.uSweep.value = this._sweepT <= 1 ? this._sweepT : -1;
      if (this._sweepT > 1) this._sweepT = -1;
    } else {
      u.uSweep.value = -1;
    }

    // --- the phrase: half a beat of black, then a new pattern ---
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    let blackout = false;
    if (phrasePhase != null) {
      blackout = phrasePhase > 1 - BLACKOUT_PHRASE_FRAC;
      if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._changePattern();
      this._lastPhrasePhase = phrasePhase;
    }

    if (this._patFade < 1) {
      this._patFade = clamp01(this._patFade + deltaTime / (this._beatPeriod * PATTERN_FADE_BEATS));
    }
    u.uPat.value = this._pat;
    u.uPatPrev.value = this._patPrev;
    u.uPatMix.value = this._patFade;

    // --- the drop ---
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._lastMode !== 'drop') {
      this._dropT = 0;
      this._pickConverge();
    }
    this._lastMode = mode;
    if (this._dropT >= 0) {
      // The brief's sequence starts on CONVERGE whatever was being danced.
      u.uPat.value = PAT_CONVERGE;
      u.uPatPrev.value = PAT_CONVERGE;
      u.uPatMix.value = 1;
    }
    this._stepDrop(deltaTime);

    // --- bands ---
    // bands.low opens and closes the whole wall by +/-18%; nothing here can
    // reach zero, so the choreography is never erased by a quiet passage.
    const low = bands && bands.low != null ? bands.low : 0.5;
    const high = bands && bands.high != null ? bands.high : 0.5;
    this._spread += (0.82 + 0.36 * low - this._spread) * Math.min(1, deltaTime / 0.25);
    u.uSpread.value = this._spread;
    u.uLensGain.value = 0.55 + 0.45 * high;

    u.uConverge.value.copy(this._converge);
    u.uConvergePrev.value.copy(this._convergePrev);
    u.uTime.value = this._time;
    u.uPulse.value = director ? director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY) : 1;
    const burstGain = 1 + this._uniforms.uBurst.value * 0.8;
    u.uMasterGain.value = blackout ? 0 : burstGain;

    // Pixels per world unit at unit view depth - the beams' screen-width
    // floor. Guarded like urlParam: this class is exercised under Node in its
    // acceptance harness, where there is no window and no pixels.
    // The REAL render buffer, not window.innerHeight: the sharp pass renders
    // at BASE_RENDER_SCALE * the tier's own resolution scale, and the beam's
    // width is specified in pixels, so taking the CSS height instead would
    // make the cone up to 30% wrong at tier 0.
    const tanHalf = Math.tan((this._camera.fov * DEG) / 2);
    const bufferHeight = getRenderBufferHeight() || 1080;
    u.uPixelScale.value = bufferHeight / (2 * tanHalf);

    if (this._probe && typeof window !== 'undefined') {
      window.__beamwall = {
        t: this._time,
        step: this._step,
        stepMix: u.uStepMix.value,
        pattern: u.uPat.value,
        patternPrev: u.uPatPrev.value,
        patternMix: u.uPatMix.value,
        sweep: u.uSweep.value,
        master: u.uMasterGain.value,
        burst: u.uBurst.value,
        white: u.uWhite.value,
        spread: u.uSpread.value,
        converge: [this._converge.x, this._converge.y, this._converge.z]
      };
    }

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }
  }

  dispose() {
    this._scene.fog = null;
    this._scene.remove(this._floor);
    this._floorGeometry.dispose();
    this._floorMaterial.dispose();
    this._scene.remove(this._backdrop);
    this._backGeometry.dispose();
    this._backMaterial.dispose();
    this._scene.remove(this._beams);
    this._beamGeometry.dispose();   // the MATERIAL is cached - see MATERIALS
    this._scene.remove(this._impacts);
    this._impactGeometry.dispose();
    this._scene.remove(this._heads);
    this._headGeometry.dispose();    // the MATERIALS are cached, not disposed
    this._scene.remove(this._yokes);
    this._yokeGeometry.dispose();
    this._scene.remove(this._truss);
    this._trussGeometry.dispose();
    this._scene.remove(this._cables);
    this._cableGeometry.dispose();
    if (typeof window !== 'undefined' && window.__beamwall) delete window.__beamwall;
  }
}
